import React from "react";
import { Modal, StyleSheet, View } from "react-native";

import {
  TModal,
  TPlan,
  TSimulationLinkAndPin,
  TClinicPatientType,
} from "../../../../utils/types";

import ShareReportPdfModal from "./ShareReportPdfModal";
import ShareEngageLinkModal from "./ShareEngageLinkModal";
import FillOutClinicInfoModal from "./FillOutClinicInfoModal";
import InviteAMemberModal from "./InviteAMemberModal";
import AddPatientOrProcedureModal from "./AddPatientOrProcedureModal";
import ConfirmUpdatePlanModal from "./ConfirmUpdatePlanModal";
import UpgradePromptModal from "./UpgradePromptModal";
import DeployWidgetNudgeModal from "./DeployWidgetNudgeModal";
import ClinicEmailVerificationNudgeModal from "./ClinicEmailVerificationNudgeModal";
import { TPatientInfo } from "../../../../services/api-clinic";
import { TClinicUsageResponse } from "../../../../services/api";

type ModalOpenerProps = {
  visible: boolean;
  modalType: TModal;
  onClose: () => void;
  addNewPatient?: ((newPatient: TPatientInfo) => void) | null;
  currentPatientUuid?: string | null;
  simulationLinkAndPin?: TSimulationLinkAndPin | null;
  planToUpgrade?: TPlan | null;
  patientType?: TClinicPatientType | null;
  usageStats?: TClinicUsageResponse | null;
  upgradePromptVariant?: "default" | "freeSimulationLimit";
};

const ModalOpener: React.FC<ModalOpenerProps> = ({
  visible,
  onClose,
  modalType,
  addNewPatient = null,
  currentPatientUuid = null,
  simulationLinkAndPin = null,
  planToUpgrade = null,
  patientType = null,
  usageStats = null,
  upgradePromptVariant = "default",
}) => {
  const handleModalClose = () => {
    onClose();
  };

  const renderModal = () => {
    switch (modalType) {
      case "AddANewPatient":
        if (!addNewPatient || !patientType) return null;
        return (
          <AddPatientOrProcedureModal
            onClose={handleModalClose}
            addNewPatient={addNewPatient}
            currentPatientUuid={null}
            addPatientOrProcedure="patient"
            patientType={patientType}
          />
        );
      case "AddANewProcedure":
        if (!currentPatientUuid || !patientType) return null;
        return (
          <AddPatientOrProcedureModal
            onClose={handleModalClose}
            addNewPatient={null}
            addPatientOrProcedure="procedure"
            currentPatientUuid={currentPatientUuid}
            patientType={patientType}
          />
        );
      case "ShareEngageLink":
        return <ShareEngageLinkModal onClose={handleModalClose} />;
      case "ShareReportPdf":
        return (
          <ShareReportPdfModal
            onClose={handleModalClose}
            simulationLinkAndPin={simulationLinkAndPin}
          />
        );
      case "FillOutClinicInfo":
        return <FillOutClinicInfoModal onClose={handleModalClose} />;
      case "InviteAMember":
        return <InviteAMemberModal onClose={handleModalClose} />;
      case "ConfirmUpdatePlan":
        if (!planToUpgrade) return null;
        return (
          <ConfirmUpdatePlanModal
            onClose={handleModalClose}
            planToUpgrade={planToUpgrade}
          />
        );
      case "UpgradePrompt":
        return (
          <UpgradePromptModal
            onClose={handleModalClose}
            usageStats={usageStats}
            variant={upgradePromptVariant}
          />
        );
      case "DeployWidgetNudge":
        return <DeployWidgetNudgeModal onClose={handleModalClose} />;
      case "ClinicEmailVerificationNudge":
        return <ClinicEmailVerificationNudgeModal onClose={handleModalClose} />;

      default:
        return null;
    }
  };

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={visible}
      onRequestClose={() => {
        if (modalType === "FillOutClinicInfo") return;
        handleModalClose();
      }}
    >
      <View style={styles.centeredView}>{renderModal()}</View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  centeredView: {
    flex: 1,
    padding: 16,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
});

export default ModalOpener;
